import React, { useEffect, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useGlobal } from '@rcg/ui-react';
import { Link, Typography } from '@rcg/ui-react/lib/components';
import TuneIcon from '@material-ui/icons/Tune';
import SwapVertIcon from '@material-ui/icons/SwapVert';
import DropdownMenu from '../UiComponents/DropDownMenu';
import useStyles from './useStyles';
import Dictionary from '../../utils/dictionary';
import ResultsCard from '../ResultsCard';
import { filteredResultsVar, filtersVar } from '../../operations/localState/reactiveVariables';
import flightSearch from '../../utils/flightSearch';
import {
  SESSION_STORAGE_BOOKING_PROFILE_NAME,
  SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS,
  SESSION_STORAGE_LAST_RETURN_FLIGHT_RESULTS,
} from '../../utils/constants';
import { getSessionStorageItem } from '../../operations/sessionStorage/utils';

const SORT_OPTION_KEYS = [
  { dictKey: 'airPriceLabel', key: 'price' },
  { dictKey: 'airDepartureEarliestLabel', key: 'departure-earliest' },
  { dictKey: 'airDepartureLatestLabel', key: 'departure-latest' },
  { dictKey: 'airArrivalEarliestLabel', key: 'arrival-earliest' },
  { dictKey: 'airArrivalLatestLabel', key: 'arrival-latest' },
  { dictKey: 'airDurationLabel', key: 'duration' },
  { dictKey: 'airStopsLabel', key: 'stops' },
];

const ResultsSuccess = ({ handleDrawerToggle, lastSearchDate, flightType }) => {
  const classes = useStyles();
  const {
    renderContent,
    data: { localizedDayjs },
  } = useGlobal();

  const sortMenuItems = SORT_OPTION_KEYS.map(({ dictKey, key }) => ({
    name: renderContent(Dictionary[dictKey].key, Dictionary[dictKey].defaultValue),
    key,
  }));
  const filteredResults = useReactiveVar(filteredResultsVar);
  const filters = useReactiveVar(filtersVar);
  const [sortOption, setSortOption] = useState({ key: 'price', name: 'Price' });
  const bookingProfile = getSessionStorageItem(SESSION_STORAGE_BOOKING_PROFILE_NAME);
  const departureFlightResults = getSessionStorageItem(
    SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS,
  );
  const returningFlightResults = getSessionStorageItem(SESSION_STORAGE_LAST_RETURN_FLIGHT_RESULTS);
  const itineraryDates =
    bookingProfile && bookingProfile.flightProfile && bookingProfile.flightProfile.flightWindows;

  const departureSailingDate = itineraryDates
    ? localizedDayjs(itineraryDates.arrival.latestDate)
    : departureFlightResults &&
      departureFlightResults.shipBrowseFlight &&
      departureFlightResults.shipBrowseFlight.sailing.sailDate;

  const departureTakeOffDate =
    departureFlightResults &&
    departureFlightResults.flightFilters &&
    departureFlightResults.flightFilters.flightTimes.takeOff.minDateTime;

  const returningSailingDate = itineraryDates
    ? localizedDayjs(itineraryDates.departure.earliestDate)
    : departureFlightResults &&
      departureFlightResults.shipBrowseFlight &&
      departureFlightResults.shipBrowseFlight.sailing.returnDate;

  const returningTakeOffDate =
    returningFlightResults && returningFlightResults.flightFilters.flightTimes.takeOff.minDateTime;

  const getEarliestTakeOffDate = (takeOffDate, sailingDate) => {
    const earliestTakeOffDate = localizedDayjs(takeOffDate).format('YYYY/MM/DD');
    const formatedTakeOffDate = localizedDayjs(earliestTakeOffDate);
    return {
      flightAvailability: localizedDayjs(takeOffDate).format('dddd[,] MMMM D[,] YYYY'),
      diffSalingAndTakeOffDates: formatedTakeOffDate.diff(sailingDate, 'day'),
    };
  };

  const departureEarliestDate = getEarliestTakeOffDate(departureTakeOffDate, departureSailingDate);
  const returningEarliestTakeOffDate =
    returningFlightResults && getEarliestTakeOffDate(returningTakeOffDate, returningSailingDate);

  const isDeparture =
    flightType === 'departure' && departureEarliestDate.diffSalingAndTakeOffDates < 0;
  const isReturning =
    flightType !== 'departure' &&
    returningEarliestTakeOffDate &&
    returningEarliestTakeOffDate.diffSalingAndTakeOffDates > 0;

  const onSelectedSortOption = (option) => {
    if (filteredResults) {
      const sortedFlights = flightSearch.sortByFlightSearch(filteredResults, option);
      filteredResultsVar([...sortedFlights]);
      const selectedSortOption = {
        key: option.key,
        name: option.name || option.option,
      };
      setSortOption(selectedSortOption);
    }
  };

  useEffect(() => {
    onSelectedSortOption(sortOption);
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    onSelectedSortOption({
      key: 'price',
      option: renderContent(Dictionary.airPriceLabel.key, Dictionary.airPriceLabel.defaultValue),
    });
    // eslint-disable-next-line
  }, [lastSearchDate]);

  return (
    <div className={classes.resultsSection}>
      <div className={classes.resultsContent}>
        <Typography
          className={classes.formatedDate}
          style={!isDeparture && !isReturning ? { display: 'none' } : {}}>
          {isDeparture &&
            `${renderContent(
              Dictionary.airEarliestFlightLabel.key,
              Dictionary.airEarliestFlightLabel.defaultValue,
            )} ${departureEarliestDate.flightAvailability}`}
          {isReturning &&
            `${renderContent(
              Dictionary.airEarliestFlightLabel.key,
              Dictionary.airEarliestFlightLabel.defaultValue,
            )} ${returningEarliestTakeOffDate.flightAvailability}`}
        </Typography>
        <div className={classes.resultsActions}>
          <div className={classes.resultsFilterMenu}>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              data-testid="open-filters-button"
              component="button"
              underline="none"
              className={`${classes.openFiltersButton}`}
              onClick={handleDrawerToggle}>
              <TuneIcon className={classes.resultsFilterMenuIcon} />
              <span>
                {renderContent(
                  Dictionary.airFilterLabel.key,
                  Dictionary.airFilterLabel.defaultValue,
                )}
              </span>
            </Link>
          </div>
          <span className={classes.resultsFlightsNumber}>
            {filteredResults.length}{' '}
            {renderContent(
              Dictionary.airResultsCountLabel.key,
              Dictionary.airResultsCountLabel.defaultValue,
            )}
          </span>
          <span className={classes.resultsSorting}>
            <span>
              {renderContent(Dictionary.airSortByLabel.key, Dictionary.airSortByLabel.defaultValue)}
              :&nbsp;&nbsp;
            </span>
            <DropdownMenu
              className={classes.sortingDropdown}
              menuItems={sortMenuItems}
              setSelectedOption={sortOption}
              id="results-sort"
              onSelectedOption={onSelectedSortOption}
            />
          </span>
        </div>
        <ResultsCard flightType={flightType} />
      </div>
      <div className={classes.mobileBar}>
        <button type="button" className={classes.mobileBarBtn} onClick={handleDrawerToggle}>
          <TuneIcon className={classes.mobileBarIcon} />
          {renderContent(Dictionary.airFilterLabel.key, Dictionary.airFilterLabel.defaultValue)}
        </button>
        <div className={classes.mobileBarDivider} />
        <div className={classes.mobileBarSortWrapper}>
          <SwapVertIcon className={classes.mobileBarIcon} />
          <DropdownMenu
            className={classes.mobileBarSortDropdown}
            menuItems={sortMenuItems}
            setSelectedOption={sortOption}
            id="results-sort-mobile"
            onSelectedOption={onSelectedSortOption}
          />
        </div>
      </div>
    </div>
  );
};

export default ResultsSuccess;
